// src/components/WhatsAppCampaigns/WhatsAppCampaigns.js
import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { getAccessJwtToken } from "../../store/globalUtilities";
import CmsContext from "../../store/cms-context";
import cloneDeep from "lodash/cloneDeep";
import WhatsAppCampaignsTable from "../WhatsAppCampaignsTable/WhatsAppCampaignsTable";
import WhatsAppCampaignsDialog from "../WhatsAppCampaignsDialog/WhatsAppCampaignsDialog";
import WhatsAppCampaignsRunDialog from "../WhatsAppCampaignsRunDialog/WhatsAppCampaignsRunDialog";
import WhatsAppCampaignsRunInsights from "../WhatsAppCampaignsRunInsights/WhatsAppCampaignsRunInsights";

const TEMPLATES_API =
  "https://fri7xzt2n6.execute-api.eu-west-2.amazonaws.com/SnagCMS/whatsapp/templates";
const CAMPAIGNS_API =
  "https://fri7xzt2n6.execute-api.eu-west-2.amazonaws.com/SnagCMS/whatsapp/campaigns";

const defaultCampaign = {
  campaign_id: null,
  campaign_name: "",
  template: null,
  status: "new",
  audience: null,
  created_at: null,
  schedule_mode: "on_demand", 
  schedule_run_at_utc: null
};

const isImmutable = (c) => {
  if (!c) return false;
  if (c.sending) return true;
  if (typeof c.status === "string" && c.status.toLowerCase() === "sent")
    return true;
  return false;
};

export default React.memo(function WhatsAppCampaigns() {
  const cmsContext = useContext(CmsContext);
  const isMountedRef = useRef(false);

  const getTid = (t) => String(t?.template_id ?? t?.id ?? "");

  const [isLoading, setIsLoading] = useState(false);
  const [campaignsRaw, setCampaignsRaw] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [campaignDialogState, setCampaignDialogState] = useState(
    cloneDeep(defaultCampaign)
  );
  const [isCampaignDialogOpen, setIsCampaignDialogOpen] = useState(false);

  // confirmation dialog for Run
  const [confirmRunOpen, setConfirmRunOpen] = useState(false);
  const [campaignToRun, setCampaignToRun] = useState(null);

  // new run dialog state
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [runCount, setRunCount] = useState(0);
  const [runLoading, setRunLoading] = useState(false);

  const [insightsOpen, setInsightsOpen] = useState(false);
  const [insightsCampaign, setInsightsCampaign] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const templatesById = useMemo(() => {
    const m = new Map();
    (templates || []).forEach((t) => {
      const key = t?.template_id ?? t?.id;   // use whichever is present
      if (key) m.set(String(key), t);
    });
    return m;
  }, [templates]);

  useEffect(() => {
    isMountedRef.current = true;
    fetchTemplates();
    fetchCampaigns();
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isMountedRef.current) return;
    const enriched = (campaignsRaw || []).map((c) => {
      const tid = c?.template_id ? String(c.template_id) : "";
      const fullTemplate = tid ? templatesById.get(tid) || null : null;
      const rawStatus = (c.status || "new").toLowerCase();
      const derivedStatus =
        rawStatus === "sent"
          ? "sent"
          : (c.schedule_mode === "scheduled" ? "scheduled" : rawStatus || "new");
      return {
        ...c,
        template: fullTemplate,                                    
        template_name: fullTemplate?.template_name || "",          
        status: derivedStatus,      
      };
    });
    setCampaigns(enriched);
  }, [campaignsRaw, templatesById]);

  async function fetchTemplates() {
    try {
      const auth = await getAccessJwtToken();
      const email = cmsContext?.current_shop?.email || "";
      const res = await fetch(TEMPLATES_API, {
        method: "GET",
        headers: {
          Authorization: auth.jwt,
          email,
        },
      });
      const data = await res.json();
      if (!isMountedRef.current) return;
      if (Array.isArray(data.templates)) {
        setTemplates(data.templates || []);
      } else {
        cmsContext.setSnackBarOpen(true, "error", "Failed to load templates.");
      }
    } catch (err) {
      console.error("fetchTemplates error:", err);
      if (isMountedRef.current) {
        cmsContext.setSnackBarOpen(true, "error", "Error fetching templates.");
      }
    }
  }

  async function fetchCampaigns() {
    setIsLoading(true);
    try {
      const auth = await getAccessJwtToken();
      const email = cmsContext?.current_shop?.email || "";
      const res = await fetch(CAMPAIGNS_API, {
        method: "GET",
        headers: {
          Authorization: auth.jwt,
          email,
        },
      });
      const data = await res.json();
      if (!isMountedRef.current) return;
      if (Array.isArray(data.campaigns)) {
        setCampaignsRaw(data.campaigns);
      } else {
        cmsContext.setSnackBarOpen(true, "error", "Failed to load campaigns.");
        setCampaignsRaw([]);
      }
    } catch (err) {
      console.error("fetchCampaigns error:", err);
      if (isMountedRef.current) {
        cmsContext.setSnackBarOpen(true, "error", "Error fetching campaigns.");
        setCampaignsRaw([]);
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }

  function openNewCampaign() {
    setCampaignDialogState({ ...cloneDeep(defaultCampaign), campaign_id: null });
    setIsCampaignDialogOpen(true);
  }

  function openEditCampaign(c) {
    if (isImmutable(c)) {
      cmsContext.setSnackBarOpen(
        true,
        "error",
        "Cannot edit a campaign that is already sent."
      );
      return;
    }
    const fullTemplate =
      (c.template && c.template.template_id
        ? c.template
        : templates.find((t) => t.template_id === c.template_id)) || null;
    setCampaignDialogState({
      campaign_id: c.campaign_id,
      campaign_name: c.campaign_name,
      template: fullTemplate,
     template_id: c.template_id,          // <-- add this
     template_name: c.template_name || "",// <-- optional, helps label fallback
      status: c.status,
      audience: c.audience,
      created_at: c.created_at,
      schedule_mode: c.schedule_mode ?? "on_demand",
      schedule_run_at_utc: c.schedule_run_at_utc ?? null,
    });
    setIsCampaignDialogOpen(true);
  }

  function openConfirmRun(campaign) {
    setCampaignToRun(campaign);
    setConfirmRunOpen(true);
  }

  async function confirmAndRun() {
    setConfirmRunOpen(false);
    if (!campaignToRun) return;
    runCampaign(campaignToRun);
    setCampaignToRun(null);
  }

  async function saveCampaign(campaign) {
    if (isImmutable(campaign)) {
      cmsContext.setSnackBarOpen(
        true,
        "error",
        "Cannot save a campaign that is already sent."
      );
      return;
    }
    const auth = await getAccessJwtToken();
    const email = cmsContext?.current_shop?.email || "";
    try {
      const isEdit = !!campaign.campaign_id;
      const tplId = getTid(campaign.template);
      const payload = {
        request_type: isEdit ? "edit" : "create",
        campaign_id: campaign.campaign_id,
        campaign_name: campaign.campaign_name,
        template_id: tplId, 
        schedule_mode: campaign.schedule_mode || "on_demand",
        schedule_run_at_utc: campaign.schedule_run_at_utc || null,
      };
      console.debug("saveCampaign payload:", payload);
      if (campaign.schedule_mode === "scheduled") {
        payload.status = "scheduled";
      }
      const res = await fetch(CAMPAIGNS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: auth.jwt,
          email,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save campaign failed");

      const resultingCampaignId = data.campaign_id || campaign.campaign_id;
      const updatedCampaign = {
        ...campaign,
        campaign_id: resultingCampaignId,
        campaign_name: campaign.campaign_name,
        template: campaign.template,
        status:
          campaign.schedule_mode === "scheduled"
            ? "scheduled"
            : (campaign.status || "new"),
      };

      if (campaign.audience) {
        await setAudience(updatedCampaign);
      }

      cmsContext.setSnackBarOpen(true, "success", data.message || "Saved!");
      await fetchCampaigns();
    } catch (e) {
      console.error("saveCampaign failed:", e);
      cmsContext.setSnackBarOpen(true, "error", e.message || "Save failed");
      throw e;
    }
  }

  async function setAudience(campaign) {
    if (isImmutable(campaign)) {
      cmsContext.setSnackBarOpen(
        true,
        "error",
        "Cannot update audience for a campaign that is already sent."
      );
      return;
    }
    const auth = await getAccessJwtToken();
    const email = cmsContext?.current_shop?.email || "";
    try {
      const aud = campaign.audience;
      if (!aud) return;

      const payload = {
        request_type: "audience",
        campaign_id: campaign.campaign_id,
        audience_type: aud.type === "all" ? "subscribed" : "csv",
      };

      if (aud.type === "csv") {
        if (aud.file instanceof File) {
          const fileBase64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result;
              if (typeof result === "string") {
                const commaIdx = result.indexOf(",");
                resolve(
                  commaIdx !== -1 ? result.slice(commaIdx + 1) : result
                );
              } else {
                reject(new Error("Failed to read file as text"));
              }
            };
            reader.onerror = () => reject(new Error("File read error"));
            reader.readAsDataURL(aud.file);
          });
          payload.file = fileBase64;
          payload.filename = aud.file.name || "audience.csv";
          if (Array.isArray(aud.raw) && aud.raw.length) {

            payload.numbers = aud.raw;
          }
        } else if (Array.isArray(aud.raw)) {
          payload.numbers = aud.raw;
        }
      }

      const res = await fetch(CAMPAIGNS_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: auth.jwt,
          email,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Set audience failed");
    } catch (e) {
      console.error("setAudience failed:", e);
      cmsContext.setSnackBarOpen(true, "error", e.message || "Audience update failed");
      throw e;
    }
  }

async function runCampaign(campaign) {
    if (isImmutable(campaign)) {
      cmsContext.setSnackBarOpen(true, "error", "Cannot run a campaign that is already sent.");
      return;
    }
    const auth = await getAccessJwtToken();
    const email = cmsContext?.current_shop?.email || "";
    setRunLoading(true);
    try {
      const payload = { request_type: "run", campaign_id: campaign.campaign_id };
      const res = await fetch(CAMPAIGNS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: auth.jwt, email },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Run failed");

      // show count in dialog
      setRunCount(data.published ?? 0);
      setRunDialogOpen(true);

      cmsContext.setSnackBarOpen(true, "success", "Campaign started");
      await fetchCampaigns();
    } catch (e) {
      console.error("runCampaign error:", e);
      cmsContext.setSnackBarOpen(true, "error", e.message || "Run failed");
    } finally {
      setRunLoading(false);
    }
  }

async function unscheduleCampaign(campaign) {
  const auth = await getAccessJwtToken();
  const email = cmsContext?.current_shop?.email || "";
  try {
    const res = await fetch(CAMPAIGNS_API, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: auth.jwt, email },
      body: JSON.stringify({ request_type: "unschedule", campaign_id: campaign.campaign_id }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Unschedule failed");
    cmsContext.setSnackBarOpen(true, "success", data.message || "Schedule removed");
    await fetchCampaigns();
  } catch (e) {
    console.error("unscheduleCampaign error:", e);
    cmsContext.setSnackBarOpen(true, "error", e.message || "Unschedule failed");
  }
}  

    function openInsights(c) {
    if (c.status.toLowerCase() !== "sent") return;
    setInsightsCampaign(c);
    setInsightsOpen(true);
  }


async function deleteCampaigns(selected) {
  if (!selected || !selected.length) return;

  const blocked = selected.filter((c) => isImmutable(c));
  const allowed = selected.filter((c) => !isImmutable(c));

  // Confirmation message summarizing the action
  const toDeleteCount = allowed.length;
  const blockedCount = blocked.length;

  if (blockedCount > 0) {
    cmsContext.setSnackBarOpen(
      true,
      "warning",
      `Skipped ${blockedCount} campaign(s) that are already sent.`
    );
  }

  if (!toDeleteCount) return;

  const confirmed = window.confirm(
    `Delete ${toDeleteCount} campaign(s)${
      blockedCount ? ` (with ${blockedCount} skipped)` : ""
    }?`
  );
  if (!confirmed) return;

  const auth = await getAccessJwtToken();
  const email = cmsContext?.current_shop?.email || "";

  try {
    const payload = {
      request_type: "delete",
      campaigns: allowed.map((c) => ({
        campaign_id: c.campaign_id || c.id,
      })),
    };

    const res = await fetch(CAMPAIGNS_API, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: auth.jwt,
        email,
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Delete failed");

    cmsContext.setSnackBarOpen(true, "success", data.message || "Deleted!");
    await fetchCampaigns();
  } catch (e) {
    console.error("deleteCampaigns error:", e);
    cmsContext.setSnackBarOpen(true, "error", e.message || "Delete failed");
  }
}

  const testedTemplates = useMemo(
    () => (templates || []).filter(t => t.tested),
    [templates]
  );
   // Templates currently used by any existing campaign
  const usedTemplateIds = useMemo(() => {
    return new Set(
      (campaigns || [])
        .map((c) => c?.template?.template_id || c?.template_id)
        .filter(Boolean)
        .map(String)
    );
  }, [campaigns]);

  // allow the currently selected template if editing, but exclude all other used ones.
  const currentTemplateId = useMemo(() => {
    const t = campaignDialogState?.template;
    const fromObject = t?.template_id ?? t?.id ?? null;
    const fromScalar = campaignDialogState?.template_id ?? null;
    return fromObject != null
      ? String(fromObject)
      : fromScalar != null
      ? String(fromScalar)
      : null;
  }, [campaignDialogState]);

  const availableTemplates = useMemo(() => {
    return (templates || [])
      .filter(t => t.tested)
      .filter(t => {
        const tid = t?.template_id ?? t?.id;
        if (!tid) return false;
        const tidStr = String(tid);
        if (currentTemplateId && tidStr === currentTemplateId) return true; // allow current
        return !usedTemplateIds.has(tidStr);
      });
  }, [templates, usedTemplateIds, currentTemplateId]);

  return (
    <>
      {isLoading ? (
        <CircularProgress
          sx={{ position: "absolute", top: "50%", left: "50%" }}
        />
      ) : (
        <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
          <Container maxWidth="lg">
            <Stack direction="column" spacing={2} mb={3}>
              <Typography variant="h5">WhatsApp Campaigns</Typography>
              <Typography variant="body1" color="textSecondary">
                <p>Campaigns enable you to combine tested message templates with a selected audience, either a segment of customers from a CSV file or all consenting customers, and send the template. <strong>Templates cannot be used across multiple campaigns. You must use a new template per campaign.</strong></p>
                <p>Once a campaign has been sent, it cannot be edited or deleted. To ensure a successful campaign, verify that your template has been fully tested and that the audience details are correct before sending the campaign.</p>
                <p>About an hour after a campaign is sent, insights will be available, providing a detailed breakdown of message volume, delivery rates and other key metrics.</p>
                <br></br>
                <p>Click <strong>“New Campaign”</strong> to begin.</p>
              </Typography>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              mb={2}
              alignItems="center"
            >
              <Button variant="contained" onClick={openNewCampaign}>
                New Campaign
              </Button>
            </Stack>

            <Paper>
              <WhatsAppCampaignsTable
                campaigns={campaigns}
                onEditCampaign={openEditCampaign}
                onRunCampaign={openConfirmRun}
                onUnscheduleCampaign={unscheduleCampaign}
                onInsightsCampaign={openInsights}
                onDeleteCampaigns={deleteCampaigns}
                page={page}
                rowsPerPage={rowsPerPage}
                setPage={setPage}
                setRowsPerPage={setRowsPerPage}
              />
              {campaigns.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography>No campaigns to show.</Typography>
                </Box>
              )}
            </Paper>
          </Container>
        </Box>
      )}

      <WhatsAppCampaignsDialog
        open={isCampaignDialogOpen}
        campaign={campaignDialogState}
        testedTemplates={availableTemplates}
        usedTemplateIds={usedTemplateIds}
        onClose={() => setIsCampaignDialogOpen(false)}
        onSave={async (c) => {
          await saveCampaign(c);
          setIsCampaignDialogOpen(false);
        }}
      />

      <Dialog
        open={confirmRunOpen}
        onClose={() => setConfirmRunOpen(false)}
        aria-labelledby="confirm-run-title"
        aria-describedby="confirm-run-description"
      >
        <DialogTitle id="confirm-run-title">Start this campaign?</DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-run-description" sx={{ mb: 1.5 }}>
            You’re about to start{" "}
            <strong>{campaignToRun?.campaign_name || "this campaign"}</strong>
            {campaignToRun?.template?.template_name
              ? ` using template “${campaignToRun.template.template_name}”. `
              : ". "}
             Once started, the campaign cannot be edited or deleted.
          </DialogContentText>
          {campaignToRun?.audience && (
            <DialogContentText sx={{ display: "block", mt: 0.5 }}>
              Audience:{" "}
              <strong>
                {campaignToRun.audience.type === "subscribed"
                  ? "All subscribed customers"
                  : "CSV list"}
              </strong>
            </DialogContentText>
          )}
          {campaignToRun?.created_at && (
            <DialogContentText sx={{ display: "block", mt: 0.5 }}>
              Created:{" "}
              <strong>{new Date(campaignToRun.created_at).toLocaleString()}</strong>
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRunOpen(false)} disabled={runLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmAndRun}
            autoFocus
            disabled={runLoading}
          >
            {runLoading ? "Starting…" : "Run now"}
          </Button>
        </DialogActions>
      </Dialog>


      <WhatsAppCampaignsRunDialog
        open={runDialogOpen}
        count={runCount}
        loading={runLoading}
        onClose={() => setRunDialogOpen(false)}
      />
      <WhatsAppCampaignsRunInsights
        open={insightsOpen}
        campaign={insightsCampaign}
        onClose={() => setInsightsOpen(false)}
      />
    </>
  );
});